import { compileTimeline, type CompiledTimeline } from '../music/compile';
import { NOTE_EVENT_CATEGORIES, STEPS_PER_BAR, type AvMusicSource, type AvSession, type EventCategory, type Instrument, type Project } from '../domain/schemas';
import { TICKS_PER_BAR, TICKS_PER_BEAT, TICKS_PER_STEP, ticksToSeconds } from './time';

/**
 * Deterministic musical event extraction (Phase 4A).
 *
 * Events come from the same compiled timeline that realtime playback and WAV export
 * schedule (`music/compile.ts`), so an extracted kick is exactly a kick that is heard.
 * There is no signal analysis: imported audio clips contribute their start position, and
 * anything inside them is marked by hand (manual markers).
 *
 * Stable identity: an event's id is derived from its musical position, never from list
 * order — `note:<instrument>:<tick>` (with `#n` for the nth simultaneous note of that
 * instrument, ordered by pitch), `beat:<n>`, `bar:<n>`, `section:<sectionId>`,
 * `clip:<clipId>`, `marker:<markerId>`. Edits that don't move anything in time
 * (velocity, single-note pitch, mixer, mute, samples, names) keep every id; edits that
 * move notes (steps, offsets, swing, arrangement order) change the moved notes' ids.
 */
export const EXTRACTION_VERSION = 1;
/** Most events one session extracts; more is reported (never silently dropped). */
export const MAX_EXTRACTED_EVENTS = 20000;

export interface MusicEvent {
  id: string;
  category: EventCategory;
  /** Exact musical position (PPQ 192). */
  tick: number;
  /** Seconds at the project tempo (derived from `tick`). */
  seconds: number;
  /** 0–1 where the source has a velocity (notes); 1 otherwise. */
  velocity: number;
  pitch?: number;
  /** Where it came from: a note (and its section), the grid, a section, a clip or a marker. */
  source: { kind: 'note'; instrument: Instrument; sectionId: string | null } | { kind: 'grid' } | { kind: 'section'; sectionId: string } | { kind: 'clip'; clipId: string } | { kind: 'marker'; markerId: string; label: string };
  version: number;
}

export interface ExtractedEvents {
  events: MusicEvent[];
  /** Sorted events per category (for fast lookup and drawing). */
  byCategory: ReadonlyMap<EventCategory, MusicEvent[]>;
  totalTicks: number;
  totalSeconds: number;
  bpm: number;
  sections: CompiledTimeline['sections'];
  truncated: boolean;
  version: number;
}

const NOTE_CATEGORIES = new Set<string>(NOTE_EVENT_CATEGORIES);

/** The compiled timeline a session's music source plays. */
export function sessionTimeline(project: Project, music: AvMusicSource): CompiledTimeline {
  const pattern = music.kind === 'pattern' ? project.patterns.find((p) => p.id === music.patternId) : undefined;
  // The song, or the song when a pattern source's pattern no longer exists.
  if (music.kind === 'song' || !pattern) return compileTimeline(project, { mode: 'song' });
  // A looping pattern played for `bars` bars: compile it as a one-section song.
  const synthetic: Project = {
    ...project,
    arrangement: { sections: [{ id: `loop_${pattern.id}`.slice(0, 64), name: pattern.name, kind: 'verse', patternId: pattern.id, bars: music.bars }] },
    audioClips: [],
  };
  return compileTimeline(synthetic, { mode: 'song' });
}

export function extractEvents(project: Project, session: Pick<AvSession, 'music' | 'markers'>): ExtractedEvents {
  const bpm = project.settings.bpm;
  const timeline = sessionTimeline(project, session.music);
  const out: MusicEvent[] = [];
  const seconds = (tick: number) => ticksToSeconds(tick, bpm);
  const sectionAt = (tick: number) => timeline.sections.find((s) => tick >= s.startStep * TICKS_PER_STEP && tick < s.endStep * TICKS_PER_STEP)?.id ?? null;
  // Notes: ordinal among simultaneous notes of one instrument (timeline is sorted by tick, instrument, pitch).
  let previous = '';
  let ordinal = 0;
  for (const n of timeline.events) {
    if (!NOTE_CATEGORIES.has(n.instrument)) continue;
    const key = `${n.instrument}:${n.tick}`;
    ordinal = key === previous ? ordinal + 1 : 0;
    previous = key;
    out.push({
      id: `note:${key}${ordinal ? `#${ordinal}` : ''}`,
      category: n.instrument as EventCategory,
      tick: n.tick,
      seconds: seconds(n.tick),
      velocity: n.velocity,
      pitch: n.pitch,
      source: { kind: 'note', instrument: n.instrument, sectionId: sectionAt(n.tick) },
      version: EXTRACTION_VERSION,
    });
  }
  // Grid: every beat and bar of the music.
  const totalTicks = timeline.totalTicks;
  for (let t = 0, i = 0; t < totalTicks; t += TICKS_PER_BEAT, i++) out.push({ id: `beat:${i}`, category: 'beat', tick: t, seconds: seconds(t), velocity: t % TICKS_PER_BAR === 0 ? 1 : 0.6, source: { kind: 'grid' }, version: EXTRACTION_VERSION });
  for (let t = 0, i = 0; t < totalTicks; t += TICKS_PER_BAR, i++) out.push({ id: `bar:${i}`, category: 'bar', tick: t, seconds: seconds(t), velocity: 1, source: { kind: 'grid' }, version: EXTRACTION_VERSION });
  for (const s of timeline.sections) out.push({ id: `section:${s.id}`, category: 'section', tick: s.startStep * TICKS_PER_STEP, seconds: seconds(s.startStep * TICKS_PER_STEP), velocity: 1, source: { kind: 'section', sectionId: s.id }, version: EXTRACTION_VERSION });
  for (const c of timeline.clips) out.push({ id: `clip:${c.clipId}`, category: 'clip', tick: c.tick, seconds: seconds(c.tick), velocity: 1, source: { kind: 'clip', clipId: c.clipId }, version: EXTRACTION_VERSION });
  for (const m of session.markers) out.push({ id: `marker:${m.id}`, category: 'manual', tick: m.tick, seconds: seconds(m.tick), velocity: 1, source: { kind: 'marker', markerId: m.id, label: m.label }, version: EXTRACTION_VERSION });

  out.sort((a, b) => a.tick - b.tick || a.category.localeCompare(b.category) || a.id.localeCompare(b.id));
  const truncated = out.length > MAX_EXTRACTED_EVENTS;
  const events = truncated ? out.slice(0, MAX_EXTRACTED_EVENTS) : out;
  const byCategory = new Map<EventCategory, MusicEvent[]>();
  for (const e of events) {
    let list = byCategory.get(e.category);
    if (!list) byCategory.set(e.category, (list = []));
    list.push(e);
  }
  return { events, byCategory, totalTicks, totalSeconds: seconds(totalTicks), bpm, sections: timeline.sections, truncated, version: EXTRACTION_VERSION };
}

/** Bars in a session's music. */
export const musicBars = (timeline: CompiledTimeline): number => timeline.totalSteps / STEPS_PER_BAR;

/** Index of the first event with tick ≥ `tick` in a sorted list (binary search). */
export function lowerBound(events: readonly { tick: number }[], tick: number): number {
  let lo = 0;
  let hi = events.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid]!.tick < tick) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}
